import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from '@my-toolbox-fe/environments/environment';
import { Filter } from '@my-toolbox-fe/type-filter';
import { BrandCountryMapping } from '@my-toolbox-fe/type-brand-country-mapping';
import { catchError, firstValueFrom, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class BrandCountryMappingService {
  private http = inject(HttpClient);

  private allowedIso2Sig: WritableSignal<string[]> = signal([]);
  private contextCountryIso2Sig: WritableSignal<string[]> = signal([]);
  private isProductRelatedSig = signal<boolean>(false);

  setContext(params: {
    userAllowedIso2: string[];
    contextCountryIso2?: string[];
    isProductRelated: boolean;
  }) {
    this.allowedIso2Sig.set((params.userAllowedIso2 ?? []).map((s) => s?.toUpperCase()));
    this.contextCountryIso2Sig.set((params.contextCountryIso2 ?? []).map((s) => s?.toUpperCase()));
    this.isProductRelatedSig.set(params.isProductRelated);
  }

  resetToNonProduct(userAllowedIso2: string[]) {
    this.setContext({ userAllowedIso2, contextCountryIso2: [], isProductRelated: false });
  }

  async fetchByBrandUid(brandUid: string): Promise<BrandCountryMapping[]> {
    return await firstValueFrom(
      this.http
        .get<BrandCountryMapping[]>(`${environment.baseUrl}/brand-country-mappings`, {
          params: { brandUid },
        })
        .pipe(catchError((e: HttpErrorResponse) => throwError(() => e))),
    );
  }

  async loadBrandAndCompute(
    brandUid: string,
  ): Promise<{ options: Filter[]; preselected: string[] }> {
    const rows = await this.fetchByBrandUid(brandUid);

    // Build MyPT -> ISO2 map from mapping rows
    const myptToIsocodeMap = new Map(rows.map((r) => [r.countryMyPitCode, r.countryIsocode]));

    // allowed contains MyPT codes -> normalize to ISO2
    const allowed = new Set(
      (this.allowedIso2Sig() ?? []).map((code) => myptToIsocodeMap.get(code ?? '') ?? code ?? ''),
    );

    // context countries already contain ISO2 codes -> use as-is
    const contextCountries = new Set(this.contextCountryIso2Sig() ?? []);
    const isProduct = this.isProductRelatedSig();

    // Options = user allowed only; value=MyPT, label=countryName
    const options = rows
      .filter((r) => allowed.has(r.countryIsocode))
      .map<Filter>((r) => ({
        code: 'country',
        value: r.countryMyPitCode,
        name: r.countryName,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Preselected = intersection(userAllowed ∩ contextCountries) mapped to MyPT (product flow only)
    const preselected = isProduct
      ? options
          .filter((o) => contextCountries.has(myptToIsocodeMap.get(o.value) ?? ''))
          .map((o) => o.value)
      : [];

    return { options, preselected };
  }
}
